// SagaPay Checkout - Main App
// Inline checkout widget for e-commerce integration

import { useEffect, useState } from 'react';
import { useCheckoutStore } from './store/checkout';
import { ThemeProvider } from './store/theme';
import { getStoreDetails, getMerchantPaymentMethods, getSession, getPaymentMethods } from './api/client';
import { IdentityStep } from './steps/IdentityStep';
import { AddressStep } from './steps/AddressStep';
import { ShippingStep } from './steps/ShippingStep';
import { PaymentStep } from './steps/PaymentStep';
import { OrderSummary } from './components/OrderSummary';
import type { CheckoutConfig } from './types/config';
import './styles/global.css';
import styles from './App.module.css';

// Global config from parent
declare global {
  interface Window {
    SagaPayCheckoutConfig?: CheckoutConfig;
  }
}

// Helper to notify parent window/callbacks
function notifyParent(type: string, data: unknown) {
  // Send to parent window (for iframe)
  if (window.parent !== window) {
    window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, '*');
  }
  
  // Dispatch custom event (for inline widget)
  window.dispatchEvent(new CustomEvent(`sagapay:${type.toLowerCase()}`, { detail: data }));
}

function CheckoutContent() {
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const initSession = useCheckoutStore((s) => s.initSession);
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setSurfboardData = useCheckoutStore((s) => s.setSurfboardData);
  const setCustomShippingOptions = useCheckoutStore((s) => s.setCustomShippingOptions);
  const setUseBuiltInShipping = useCheckoutStore((s) => s.setUseBuiltInShipping);
  const setSkipShipping = useCheckoutStore((s) => s.setSkipShipping);
  const setTheme = useCheckoutStore((s) => s.setTheme);
  const theme = useCheckoutStore((s) => s.theme);
  const merchant = useCheckoutStore((s) => s.merchant);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  // Parse initialization config from various sources
  const getCheckoutConfig = (): Partial<CheckoutConfig> => {
    // 1. Check for global config
    if (window.SagaPayCheckoutConfig) {
      return window.SagaPayCheckoutConfig;
    }

    // 2. Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session') || params.get('sessionId');
    
    // If we have a session ID, we'll fetch the rest from API
    if (sessionId) {
      return {
        sessionId,
        partnerId: params.get('partnerId') || 'sagapay',
        merchantId: params.get('merchantId') || '',
        storeId: params.get('storeId') || '',
      };
    }

    // 3. Check for base64 encoded config in URL
    const configParam = params.get('config');
    if (configParam) {
      try {
        return JSON.parse(atob(configParam));
      } catch (e) {
        console.error('Failed to parse config:', e);
      }
    }

    // 4. Demo mode with test data
    return {
      sessionId: 'demo-session',
      partnerId: 'sagapay',
      merchantId: 'demo-merchant',
      storeId: 'demo-store',
    };
  };

  // Listen for PostMessage config from SDK
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle config from SDK
      if (event.data?.type === 'SAGAPAY_CONFIG') {
        const payload = event.data.payload;
        
        // Apply theme
        if (payload.theme) {
          setTheme(payload.theme);
        }
        
        // Apply custom shipping
        if (payload.customShipping) {
          setCustomShippingOptions(payload.customShipping);
          setUseBuiltInShipping(false);
        }
        
        // Apply customer data
        if (payload.customer) {
          setCustomer({
            email: payload.customer.email || '',
            name: `${payload.customer.firstName || ''} ${payload.customer.lastName || ''}`.trim(),
            phone: payload.customer.phone || '',
          });
        }
      }
      
      // Handle legacy config format
      if (event.data?.type === 'SAGAPAY_CHECKOUT_CONFIG') {
        window.SagaPayCheckoutConfig = event.data.config;
        // Trigger re-initialization
        initializeCheckout();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Initialize checkout
  const initializeCheckout = async () => {
    setIsInitializing(true);
    setInitError(null);

    try {
      const config = getCheckoutConfig();
      const sessionId = config.sessionId || 'demo-session';
      const partnerId = config.partnerId || 'sagapay';
      const merchantId = config.merchantId || 'demo-merchant';
      const storeId = config.storeId || 'demo-store';

      // If we have a real session ID, fetch session data from API
      let cartData = config.cart;
      let customerData = config.customer;

      if (sessionId && sessionId !== 'demo-session') {
        try {
          const sessionResponse = await getSession(sessionId);
          if (sessionResponse.data) {
            const sessionData = sessionResponse.data as any;
            if (sessionData.cart) {
              cartData = sessionData.cart;
            }
            if (sessionData.customer) {
              customerData = sessionData.customer;
            }
          }
        } catch (e) {
          console.warn('Could not fetch session, using provided config:', e);
        }
      }

      // Use demo cart if no cart data provided
      if (!cartData) {
        cartData = {
          items: [
            {
              id: 'demo-1',
              name: 'Demo Produkt',
              quantity: 1,
              unitPrice: { amount: 59900, currency: 'NOK' },
              imageUrl: 'https://via.placeholder.com/80',
            },
          ],
          totalAmount: { amount: 59900, currency: 'NOK' },
          totalItems: 1,
        };
      }

      // Initialize with cart and session data
      initSession(sessionId, {
        cart: cartData,
        merchant: {
          merchantId,
          merchantName: 'Laster...',
          features: {
            enableQuickPay: true,
            enableAddressSaving: true,
            enableVipps: true,
            collectPhone: true,
          },
        },
      });

      // Apply custom shipping options if provided
      if (config.customShipping && config.customShipping.length > 0) {
        setCustomShippingOptions(config.customShipping);
        setUseBuiltInShipping(false);
      } else if (config.useBuiltInShipping !== undefined) {
        setUseBuiltInShipping(config.useBuiltInShipping);
      }
      
      // Skip shipping for digital products
      if (config.skipShipping) {
        setSkipShipping(true);
      }
      
      // Apply theme configuration
      if (config.theme) {
        setTheme(config.theme);
      }

      // Set customer if provided
      if (customerData?.email) {
        setCustomer({
          email: customerData.email,
          name: customerData.name || '',
          phone: customerData.phone || '',
        });
      }

      // Notify parent that checkout is ready
      notifyParent('CHECKOUT_READY', { sessionId, step: 'identity' });

      // Fetch store details and payment methods from Surfboard API
      const [storeResponse, paymentMethodsResponse] = await Promise.all([
        getStoreDetails(partnerId, merchantId, storeId),
        getMerchantPaymentMethods(merchantId),
      ]);

      // Extract activated payment methods
      const activatedMethods = paymentMethodsResponse.data?.data?.map(
        (m) => m.paymentMethod
      ) || ['CARD', 'VIPPS'];

      // Fetch Surfboard SDK data for payment initialization
      // This is done after session is created and returns orderId, nonce, publicKey
      if (sessionId && sessionId !== 'demo-session') {
        try {
          const paymentDataResponse = await getPaymentMethods(sessionId);
          if (paymentDataResponse.data) {
            const { surfboardOrderId, surfboardNonce, publicKey } = paymentDataResponse.data;
            if (surfboardOrderId && surfboardNonce && publicKey) {
              setSurfboardData(surfboardOrderId, surfboardNonce, publicKey);
              console.log('[App] Surfboard SDK data loaded:', { surfboardOrderId, publicKey: publicKey.substring(0, 20) + '...' });
            }
          }
        } catch (e) {
          console.warn('Could not fetch payment methods / Surfboard data:', e);
        }
      }

      if (storeResponse.data?.data) {
        const storeData = storeResponse.data.data;
        // Update merchant with real store info and payment methods
        initSession(sessionId, {
          cart: cartData,
          merchant: {
            merchantId: storeData.merchantId,
            merchantName: storeData.name,
            features: {
              enableQuickPay: true,
              enableAddressSaving: true,
              enableVipps: activatedMethods.includes('VIPPS'),
              collectPhone: true,
            },
            storeInfo: {
              storeId: storeData.storeId,
              name: storeData.name,
              email: storeData.email,
              phone: storeData.phone,
              merchantWebshopURL: storeData.onlineInfo?.merchantWebshopURL,
              termsAndConditionsURL: storeData.onlineInfo?.termsAndConditionsURL,
              privacyPolicyURL: storeData.onlineInfo?.privacyPolicyURL,
            },
            activatedPaymentMethods: activatedMethods,
          },
        });
      } else {
        // Still update with payment methods even if store details failed
        initSession(sessionId, {
          cart: cartData,
          merchant: {
            merchantId,
            merchantName: 'Butikk',
            features: {
              enableQuickPay: true,
              enableAddressSaving: true,
              enableVipps: activatedMethods.includes('VIPPS'),
              collectPhone: true,
            },
            activatedPaymentMethods: activatedMethods,
          },
        });
      }

    } catch (error) {
      console.error('Failed to initialize checkout:', error);
      setInitError('Kunne ikke initialisere checkout. Vennligst prøv igjen.');
    }

    setIsInitializing(false);
  };

  useEffect(() => {
    initializeCheckout();
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 'identity':
        return <IdentityStep />;
      case 'address':
        return <AddressStep />;
      case 'shipping':
        return <ShippingStep />;
      case 'payment':
        return <PaymentStep />;
      default:
        return <IdentityStep />;
    }
  };

  // Loading state
  if (isInitializing) {
    return (
      <div className={styles.checkout}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Laster checkout...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (initError) {
    return (
      <div className={styles.checkout}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p className={styles.errorText}>{initError}</p>
          <button className={styles.retryButton} onClick={() => initializeCheckout()}>
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  // Get theme config for conditional rendering
  const showPoweredBy = theme?.showPoweredBy !== false;
  const showOrderSummary = theme?.showOrderSummary !== false;
  const merchantLogo = theme?.logoUrl || merchant?.logoUrl;

  return (
    <div className={styles.checkout}>
      {/* Optional merchant logo header */}
      {merchantLogo && (
        <header className={styles.merchantHeader} style={{ justifyContent: theme?.logoPosition || 'left' }}>
          <img 
            src={merchantLogo} 
            alt={merchant?.merchantName || 'Logo'} 
            className={styles.merchantLogo}
            style={{ height: theme?.logoHeight || 32 }}
          />
        </header>
      )}
      
      <main className={styles.main}>
        <div className={styles.content}>
          {renderStep()}
        </div>
      </main>
      
      {showOrderSummary && <OrderSummary />}
      
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <a 
            href={merchant?.storeInfo?.termsAndConditionsURL || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Kjøpsvilkår
          </a>
          <span>•</span>
          <a 
            href={merchant?.storeInfo?.privacyPolicyURL || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Personvernerklæring
          </a>
        </div>
        {showPoweredBy && (
          <div className={styles.poweredBy}>
            <span>Drevet av</span>
            <img 
              src="https://res.cloudinary.com/martinsurf/image/upload/v1763562569/surfboardmerchant/cexdplrnd3tr54xhjjvt.png" 
              alt="Saga" 
              className={styles.sagaLogo}
            />
          </div>
        )}
      </footer>
    </div>
  );
}

// Main App with ThemeProvider wrapper
export default function App() {
  const theme = useCheckoutStore((s) => s.theme);
  
  return (
    <ThemeProvider config={theme || undefined}>
      <CheckoutContent />
    </ThemeProvider>
  );
}